import React, { forwardRef, useContext, useEffect } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Slide, { SlideProps } from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { NotificationsContext } from '../contexts/NotificationsContext';
import '../stylesheets/notificationsBar.scss';
import { PositionKey, positions } from '../utils/notificationsBarHelpers';
import useTranslate from '../customHooks/useTranslate';

interface NotificationsBarProps {
    alertProps?: {
        severity?: string;
        isMultiline?: boolean;
        [key: string]: any;
    };
    snackbarProps?: {
        position?: number;
        autoHideDuration?: number | undefined;
        transitionDirection?: string;
        [key: string]: any;
    };
}

const NotificationsBar = ({
    alertProps: defaultAlertProps = {
        severity: 'error',
        isMultiline: false,
    },
    snackbarProps: defaultSnackbarProps = {
        position: 5,
        autoHideDuration: 6000,
    },
}: NotificationsBarProps) => {
    const {
        message,
        deleteNotificationMessage,
        snackbarProps = {},
        alertProps,
    } = useContext(NotificationsContext);
    const translate = useTranslate();

    const { isMultiline: defaultIsMultiline, ...restDefaultAlertProps } =
        defaultAlertProps;

    const { position: defaultPosition, ...restDefaultSnackbarProps } =
        defaultSnackbarProps;

    const { isMultiline: customIsMultiline, ...restAlertProps } = alertProps;

    const {
        transitionDirection: customTransitionDirection = '',
        ...restSnackbarProps
    } = snackbarProps;

    const Alert = React.forwardRef<HTMLDivElement, any>(function Alert(
        _props,
        ref
    ) {
        return (
            <MuiAlert elevation={6} ref={ref} variant='filled' {..._props} />
        );
    });

    const messagePosition = (snackbarProps?.position ??
        defaultPosition) as PositionKey;
    const position = positions[messagePosition];

    const { anchorOrigin } = position;

    const transitionDirection =
        customTransitionDirection || position.transitionDirection;

    const TransitionComponent = forwardRef<HTMLDivElement, SlideProps>(
        function TransitionComponent(props, ref) {
            return (
                <Slide {...props} direction={transitionDirection} ref={ref} />
            );
        }
    );

    const severity = alertProps.severity || defaultAlertProps.severity;
    const isMultiline = defaultIsMultiline || customIsMultiline;

    useEffect(() => {
        return deleteNotificationMessage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeText = translate('close');

    return (
        <>
            {message && (
                <Snackbar
                    {...restDefaultSnackbarProps}
                    open
                    onClose={deleteNotificationMessage}
                    TransitionComponent={TransitionComponent}
                    anchorOrigin={anchorOrigin}
                    {...restSnackbarProps}
                >
                    <Alert
                        {...restDefaultAlertProps}
                        classes={{
                            icon: `notifications-bar__icon ${severity}`,
                            root: `notifications-bar ${severity} ${
                                isMultiline ? 'multiline' : ''
                            }`,
                            action: `notifications-bar__action`,
                            message: 'notifications-bar__message',
                        }}
                        action={
                            <IconButton
                                onClick={deleteNotificationMessage}
                                title={closeText}
                            >
                                <CloseIcon />
                            </IconButton>
                        }
                        {...restAlertProps}
                    >
                        {translate(message)}
                    </Alert>
                </Snackbar>
            )}
        </>
    );
};

export default NotificationsBar;
